import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ElementsConsumer } from '@stripe/react-stripe-js';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { fetchPayments } from '../../actions/payments';
import { fetchUser } from '../../actions/user';
import api from '../../apis/api';
import statuses from './statuses';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const PaymentConfirm = ({
  stripe,
  elements,
  open,
  onCancel,
  amount,
  credits,
  paymentIntent,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const paymentMethods = useSelector((state) => state.paymentMethods);
  const paymentMethod = _.filter(paymentMethods, (method) => {
    return method.id === user.primary_pm;
  })[0];

  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    if (!open) {
      setPaymentError(null);
    }
  }, [open]);

  const getPayment = () => {
    setTimeout(async () => {
      const response = await api.get(
        `/billing/payment/${paymentIntent.payment_uuid}`
      );
      if (response.status === statuses.PENDING) {
        getPayment();
      } else {
        dispatch(fetchPayments());
        dispatch(fetchUser());
        setSubmitting(false);
        onCancel();
      }
    }, 1000);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await stripe.confirmCardPayment(
        paymentIntent.client_secret
      );
      if (result.error) {
        setPaymentError(result.error.message);
        setSubmitting(false);
        window.gtag('event', 'purchase_failed', {
          currency: 'USD',
          value: amount,
          customer_uuid: user.id,
        });
      } else {
        window.gtag('event', 'purchase', {
          currency: 'USD',
          value: amount,
          customer_uuid: user.id,
          transaction_id: paymentIntent.payment_uuid,
        });
        getPayment();
      }
    } catch (error) {
      console.log(error);
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    onCancel();
    setPaymentError(null);
    const response = await api.get(
      `/billing/payment/${paymentIntent.payment_uuid}`
    );
    if (response.status !== statuses.FAILED) {
      const result = await api.delete(
        `/billing/payment/${paymentIntent.payment_uuid}`
      );
      if (result.error) {
        setPaymentError(result.error.message);
        setSubmitting(false);
      } else {
        dispatch(fetchPayments());
        dispatch(fetchUser());
      }
    } else {
      dispatch(fetchPayments());
      dispatch(fetchUser());
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Confirm your Purchase</DialogTitle>
      <DialogContent>
        {paymentError ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography variant='subtitle1' gutterBottom>
              Charge Failed
            </Typography>
            {paymentError}
          </Alert>
        ) : !paymentMethod ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography variant='subtitle1' gutterBottom>
              Charge Failed
            </Typography>
            You do not have a primary payment method
          </Alert>
        ) : (
          <Alert severity='info' sx={{ mb: 2 }}>
            <Typography variant='subtitle1' gutterBottom>
              Amount to be charged: {formatter.format(amount)}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>You will receive {credits} credits</li>
              <li>
                Your card ending in {paymentMethod.card.last4} will be charged.
              </li>
            </ul>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <Button
            variant='outlined'
            onClick={handleClose}
            disabled={submitting}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='brand'
            onClick={handleSubmit}
            disabled={Boolean(paymentError || !paymentMethod) || submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <CheckIcon />
              )
            }
            sx={{ ml: 'auto' }}
          >
            Pay Now
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const PaymentConfirmWithStripe = (props) => (
  <ElementsConsumer>
    {({ stripe, elements }) => (
      <PaymentConfirm stripe={stripe} elements={elements} {...props} />
    )}
  </ElementsConsumer>
);

export default PaymentConfirmWithStripe;
